/**
 * Product Card Swatches Navigation
 * Handles horizontal scroll navigation for color swatches
 * - Detecta overflow correctamente después de que las imágenes se carguen
 * - Oculta flechas cuando no hay suficientes swatches
 * - Maneja scroll suave
 */

class ProductCardSwatches {
  constructor(container) {
    this.container = container;
    this.scrollableArea = container.querySelector('.product-card-swatches--scrollable');
    this.leftArrow = container.querySelector('.product-card-swatches--arrow-left');
    this.rightArrow = container.querySelector('.product-card-swatches--arrow-right');
    
    if (!this.scrollableArea) return;
    
    this.init();
  }
  
  init() {
    // Check overflow multiple times to ensure images are loaded
    this.checkOverflowWithRetry();
    
    // Event listeners
    if (this.leftArrow) {
      this.leftArrow.addEventListener('click', (e) => {
        e.preventDefault();
        this.scroll('left');
      });
    }
    
    if (this.rightArrow) {
      this.rightArrow.addEventListener('click', (e) => {
        e.preventDefault();
        this.scroll('right');
      });
    }
    
    // Update arrow states on scroll
    this.scrollableArea.addEventListener('scroll', () => this.updateArrowStates());
    
    // Re-check on resize with debounce
    let resizeTimeout;
    window.addEventListener('resize', () => {
      clearTimeout(resizeTimeout);
      resizeTimeout = setTimeout(() => {
        this.checkOverflow();
        this.updateArrowStates();
      }, 150);
    });
    
    // Use IntersectionObserver to detect when card becomes visible
    if ('IntersectionObserver' in window) {
      const observer = new IntersectionObserver((entries) => {
        entries.forEach(entry => {
          if (entry.isIntersecting) {
            // Wait a bit for rendering to complete
            setTimeout(() => this.checkOverflow(), 100);
          }
        });
      }, { threshold: 0.1 });
      
      observer.observe(this.container);
    }
  }
  
  /**
   * Check overflow with multiple retries to ensure proper detection
   */
  checkOverflowWithRetry() {
    // Immediate check
    this.checkOverflow();
    
    // Check again after short delays (for images/CSS to load)
    const delays = [50, 150, 300, 500];
    delays.forEach(delay => {
      setTimeout(() => this.checkOverflow(), delay);
    });
    
    // Also check when all images are loaded
    const images = this.container.querySelectorAll('img');
    if (images.length > 0) {
      Promise.all(
        Array.from(images).map(img => {
          if (img.complete) return Promise.resolve();
          return new Promise(resolve => {
            img.addEventListener('load', resolve, { once: true });
            img.addEventListener('error', resolve, { once: true });
          });
        })
      ).then(() => {
        this.checkOverflow();
      });
    }
  }
  
  /**
   * Check if swatches area has overflow
   */
  checkOverflow() {
    if (!this.scrollableArea) return;
    
    // Force reflow to get accurate measurements
    this.scrollableArea.offsetWidth;
    
    const scrollWidth = this.scrollableArea.scrollWidth;
    const clientWidth = this.scrollableArea.clientWidth;
    
    // Add a small tolerance (2px) for rounding errors
    const hasOverflow = scrollWidth > (clientWidth + 2);
    
    // Update container class
    this.container.classList.toggle('has-overflow', hasOverflow);
    
    // Show/hide arrows based on overflow
    if (!hasOverflow) {
      // No overflow - hide arrows
      if (this.leftArrow) {
        this.leftArrow.style.display = 'none';
      }
      if (this.rightArrow) {
        this.rightArrow.style.display = 'none';
      }
    } else {
      // Has overflow - show arrows (CSS will control via has-overflow class)
      if (this.leftArrow) {
        this.leftArrow.style.display = '';
      }
      if (this.rightArrow) {
        this.rightArrow.style.display = '';
      }
      
      // Update arrow states immediately
      this.updateArrowStates();
    }
    
    // Debug logging (remove in production)
    if (window.location.search.includes('debug')) {
      console.log('Swatch overflow check:', {
        scrollWidth,
        clientWidth,
        hasOverflow,
        container: this.container
      });
    }
  }
  
  /**
   * Update arrow states based on scroll position
   */
  updateArrowStates() {
    if (!this.scrollableArea) return;
    
    const { scrollLeft, scrollWidth, clientWidth } = this.scrollableArea;
    
    // Small tolerance for rounding
    const tolerance = 2;
    const atStart = scrollLeft <= tolerance;
    const atEnd = scrollLeft + clientWidth >= scrollWidth - tolerance;
    
    // Update container classes
    this.container.classList.toggle('at-start', atStart);
    this.container.classList.toggle('at-end', atEnd);
    
    // Visual feedback for arrows
    if (this.leftArrow) {
      this.leftArrow.style.opacity = atStart ? '0.2' : '1';
      this.leftArrow.style.pointerEvents = atStart ? 'none' : 'auto';
      this.leftArrow.setAttribute('aria-disabled', atStart ? 'true' : 'false');
    }
    
    if (this.rightArrow) {
      this.rightArrow.style.opacity = atEnd ? '0.2' : '1';
      this.rightArrow.style.pointerEvents = atEnd ? 'none' : 'auto';
      this.rightArrow.setAttribute('aria-disabled', atEnd ? 'true' : 'false');
    }
  }
  
  /**
   * Scroll swatches in specified direction
   */
  scroll(direction) {
    if (!this.scrollableArea) return;
    
    // Scroll by 70% of visible width
    const scrollAmount = this.scrollableArea.clientWidth * 0.7;
    const currentScroll = this.scrollableArea.scrollLeft;
    
    const targetScroll = direction === 'left' 
      ? Math.max(0, currentScroll - scrollAmount)
      : currentScroll + scrollAmount;
    
    this.scrollableArea.scrollTo({
      left: targetScroll,
      behavior: 'smooth'
    });
  }
}

/**
 * Initialize all swatch containers on the page
 */
function initSwatches() {
  const containers = document.querySelectorAll('.product-card-swatches--container');
  
  containers.forEach(container => {
    // Avoid re-initializing
    if (container.dataset.swatchesInitialized) return;
    container.dataset.swatchesInitialized = 'true';
    
    new ProductCardSwatches(container);
  });
}

// Run on page load
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initSwatches);
} else {
  initSwatches();
}

// Re-initialize on dynamic content load (AJAX, filters, Shopify sections)
document.addEventListener('shopify:section:load', initSwatches);

// Also listen for custom events (e.g., from Infinite Scroll, Collection Filters)
document.addEventListener('theme:products:loaded', initSwatches);

// Export for manual initialization if needed
window.ProductCardSwatches = ProductCardSwatches;
window.initProductCardSwatches = initSwatches;
